import { useTranslation } from "react-i18next";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import { Autocomplete, Box } from "@mui/material";
import FormGridRow from "../../../common/components/functional/formGridRow/FormGridRow";
import InfoTooltip from "../../../common/components/functional/InfoTooltip/InfoTooltip";
import { CustomButton } from "../../../common/components/functional/customButton/CustomButton";
import { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { Clear as DeleteIcon } from "@mui/icons-material";
import { Controller, useFormContext } from "react-hook-form";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";
import { useAppSelector } from "../../../app/hooks";
import { selectAllFormattedCustomerUsers } from "../customerUsersSlice";

const LABEL_SIZE = 2.8;
const FIELD_SIZE = 9.2;

type Props = {
  tooltipTitle?: string;
};

const InvoiceReferencesSection = ({ tooltipTitle }: Props) => {
  const [edit, setEdit] = useState(false);
  const { t } = useTranslation();
  const { control, setValue, watch } = useFormContext();

  const invoiceReferences = watch("invoiceReferences");
  const customerUserOptions = useAppSelector(selectAllFormattedCustomerUsers());

  useEffect(() => {
    // show fields if invoiceReferences exists
    setEdit(!!invoiceReferences);
  }, [invoiceReferences]);

  const handleClick = () => {
    if (edit) {
      // remove invoiceReferences and invoiceContactPerson
      setValue("invoiceReferences", null, { shouldDirty: true });
      setValue("invoiceContactPerson", null, { shouldDirty: true });
    } else {
      const emptyInvoiceReferences = {
        orderIdentifier: "",
        agreementIdentifier: "",
        sellerReferenceIdentifier: "",
        buyerReferenceIdentifier: "",
      };
      setValue("invoiceReferences", emptyInvoiceReferences, {
        shouldDirty: true,
      });
    }

    setEdit((prev) => !prev);
  };

  const buttonBaseProps = {
    sx: { borderRadius: "15px", height: "30px" },
    onClick: handleClick,
  };

  const customButton = (
    <CustomButton
      {...buttonBaseProps}
      startIcon={edit ? <DeleteIcon /> : <AddIcon color="primary" />}
      variant="outlined"
      color={edit ? "error" : "primary"}
    >
      {edit
        ? t("customers:companyInfo.removeReferences")
        : t("customers:companyInfo.addReferences")}
    </CustomButton>
  );

  const Tooltip = () => (
    <>
      {tooltipTitle ? (
        <Box display="flex" alignItems="center" marginLeft="13px">
          <InfoTooltip title={tooltipTitle} />
        </Box>
      ) : null}
    </>
  );

  const fields = [
    {
      label: t("customers:costCenters.contactPerson"),
      name: "invoiceContactPerson",
      type: "autocomplete",
    },
    {
      label: t("customers:costCenters.orderIdentifier"),
      name: "invoiceReferences.orderIdentifier",
      type: "text",
    },
    {
      label: t("customers:costCenters.agreementIdentifier"),
      name: "invoiceReferences.agreementIdentifier",
      type: "text",
    },
    {
      label: t("customers:costCenters.sellerReferenceIdentifier"),
      name: "invoiceReferences.sellerReferenceIdentifier",
      type: "text",
    },
    {
      label: t("customers:costCenters.buyerReferenceIdentifier"),
      name: "invoiceReferences.buyerReferenceIdentifier",
      type: "text",
    },
  ];

  return (
    <FormSection
      title={t("customers:companyInfo:invoiceReferencesTitle")}
      infoTooltip={<Tooltip />}
      margin
      customButton={customButton}
    >
      {edit &&
        !!invoiceReferences &&
        fields.map(({ label, name, type }) => (
          <FormGridRow
            key={name}
            label={label}
            labelSize={LABEL_SIZE}
            fieldSize={FIELD_SIZE}
          >
            <Controller
              name={name}
              control={control}
              render={({ field }) => {
                if (type === "autocomplete") {
                  return (
                    <Autocomplete
                      value={field.value}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, selectedValue) =>
                        option.id === selectedValue.id
                      }
                      onChange={(_, newValue) => field.onChange(newValue)}
                      options={customerUserOptions.sort((a, b) =>
                        a.name.localeCompare(b.name, "fi"),
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.name}
                        </li>
                      )}
                      renderInput={(params) => <CommonTextField {...params} />}
                    />
                  );
                }
                return <CommonTextField {...field} fullWidth size="small" />;
              }}
            />
          </FormGridRow>
        ))}
    </FormSection>
  );
};

export default InvoiceReferencesSection;
